import React, { useState, useEffect } from "react";
import { Box, TextField, Select, MenuItem, FormControl, InputLabel, Grid, Typography, FormControlLabel, Switch, Autocomplete, Alert } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import api from "../../services/api";

const SubjectAllocationForm = ({ formData, onFormChange, onAllAssigned }) => {
  const [teachers, setTeachers] = useState([]);
  const [subjectPapers, setSubjectPapers] = useState([]);
  const [terms, setTerms] = useState([]);
  const [existingAllocations, setExistingAllocations] = useState([]);
  const [allAllocations, setAllAllocations] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get('/report-card/teachers/active'),
      api.get('/report-card/class-stream-papers', { params: { per_page: 500 } }),
      api.get('/academic/terms'),
    ]).then(([t, sp, tr]) => {
      setTeachers(Array.isArray(t.data) ? t.data : []);
      setSubjectPapers(Array.isArray(sp.data?.data) ? sp.data.data : Array.isArray(sp.data) ? sp.data : []);
      const termsData = Array.isArray(tr.data?.data) ? tr.data.data : Array.isArray(tr.data) ? tr.data : [];
      setTerms(termsData);
      if (!formData.term_id && termsData.length > 0) {
        const today = new Date();
        const current = termsData.find(t => new Date(t.start_date) <= today && new Date(t.end_date) >= today) || termsData[0];
        onFormChange({ term_id: current.id, start_date: current.start_date || '', end_date: current.end_date || '' });
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!formData.teacher_id || !formData.academic_year) { setExistingAllocations([]); return; }
    const params = { teacher_id: formData.teacher_id, academic_year: formData.academic_year, per_page: 500 };
    if (formData.term_id) params.term_id = formData.term_id;
    api.get('/report-card/subject-teacher-allocations', { params })
      .then(res => setExistingAllocations(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => {});
  }, [formData.teacher_id, formData.academic_year, formData.term_id]);

  useEffect(() => {
    if (!formData.academic_year) { setAllAllocations([]); return; }
    const params = { academic_year: formData.academic_year, is_primary: 1, per_page: 500 };
    if (formData.term_id) params.term_id = formData.term_id;
    api.get('/report-card/subject-teacher-allocations', { params })
      .then(res => setAllAllocations(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => {});
  }, [formData.academic_year, formData.term_id]);

  const assignedPaperIds = new Set(existingAllocations.map(a => a.class_stream_subject_paper_id));
  const availablePapers = subjectPapers.filter(sp => !assignedPaperIds.has(sp.id));
  const allAssigned = formData.teacher_id && formData.academic_year && subjectPapers.length > 0 && availablePapers.length === 0;
  const primaryPaperIds = new Set(allAllocations.map(a => a.class_stream_subject_paper_id));
  const selectedHasPrimary = formData.is_primary && formData.class_stream_subject_paper_id && primaryPaperIds.has(formData.class_stream_subject_paper_id);

  useEffect(() => { onAllAssigned?.(allAssigned); }, [allAssigned]);

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: 'black' }}>Allocation Details</Typography>
      {allAssigned && <Alert severity="warning" sx={{ mb: 2 }}>All subject papers have already been assigned to this teacher for the selected year/term.</Alert>}
      {selectedHasPrimary && <Alert severity="warning" sx={{ mb: 2 }}>This subject paper already has a primary teacher for the selected year/term. Saving will create a secondary assignment.</Alert>}
      <Grid container spacing={3}>
        <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
          <Autocomplete
            options={teachers}
            getOptionLabel={(t) => `${t.full_name || `${t.last_name} ${t.first_name}`} (${t.staff_number})`}
            value={teachers.find(t => t.id === formData.teacher_id) || null}
            onChange={(_, val) => onFormChange({ teacher_id: val ? val.id : '' })}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            renderInput={(params) => <TextField {...params} label="Teacher" required />}
          />
        </Grid>
        <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
          <Autocomplete
            options={availablePapers}
            getOptionLabel={(sp) => {
              const className = sp.class?.name || sp.class?.class_name || 'Unknown Class';
              const streamName = sp.stream?.name ? `- ${sp.stream.name}` : '';
              const subjectName = sp.subject_paper?.subject?.subject_name || 'Unknown Subject';
              const paperName = sp.subject_paper?.paper_name || '';
              return `${className} ${streamName} - ${subjectName} (${paperName})`;
            }}
            value={subjectPapers.find(sp => sp.id === formData.class_stream_subject_paper_id) || null}
            onChange={(_, val) => onFormChange({ class_stream_subject_paper_id: val ? val.id : '' })}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            renderInput={(params) => <TextField {...params} label="Subject Paper" required />}
          />
        </Grid>
        <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
          <TextField fullWidth type="number" label="Academic Year" value={formData.academic_year} onChange={(e) => onFormChange({ academic_year: e.target.value })} inputProps={{ min: 2020, max: new Date().getFullYear() }} required />
        </Grid>
        <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
          <Autocomplete
            options={terms}
            getOptionLabel={(t) => t.term_name || t.name || ''}
            value={terms.find(t => t.id === formData.term_id) || null}
            onChange={(_, val) => {
              onFormChange({ term_id: val ? val.id : '', ...(val ? { start_date: val.start_date || '', end_date: val.end_date || '' } : {}) });
            }}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            renderInput={(params) => <TextField {...params} label="Term" />}
          />
        </Grid>
        <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker label="Start Date" value={formData.start_date ? dayjs(formData.start_date) : null} onChange={(v) => onFormChange({ start_date: v ? v.format('YYYY-MM-DD') : '' })} slotProps={{ textField: { fullWidth: true, required: true } }} />
          </LocalizationProvider>
        </Grid>
        <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker label="End Date" value={formData.end_date ? dayjs(formData.end_date) : null} onChange={(v) => onFormChange({ end_date: v ? v.format('YYYY-MM-DD') : '' })} slotProps={{ textField: { fullWidth: true } }} />
          </LocalizationProvider>
        </Grid>
        <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
          <FormControlLabel 
            control={
              <Switch 
                checked={Boolean(formData.is_primary)} 
                onChange={(e) => onFormChange({ is_primary: e.target.checked })} 
              />
            } 
            label="Primary Subject Teacher" 
          />
        </Grid>
        <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
          <FormControlLabel control={<Switch checked={formData.is_active} onChange={(e) => onFormChange({ is_active: e.target.checked })} />} label="Active" />
        </Grid>
      </Grid>
    </Box>
  );
};

export default SubjectAllocationForm;
